import { useCallback, useMemo } from "react";
import type { ActivityTimelineData } from "@/hooks/api/use-account-activities";
import {
  type FirstTouchpointResponse,
  useAccountFirstTouchpointTimeline,
} from "@/hooks/api/use-account-first-touchpoint-timeline";
import {
  type Opportunity,
  useAccountOpportunities,
} from "@/hooks/api/use-account-opportunities";
import type { Person } from "@/hooks/api/use-account-persons";
import { useBuyingGroupSelection } from "@/hooks/use-buying-group-selection";
import { useWindowWidth } from "@/hooks/use-window-width";
import { getTimestamp } from "@/lib/date-formatting";
import {
  formatActivityChartData,
  formatChartDomainAndTicks,
  formatFirstTouchpoints,
  formatOpportunityData,
  getOutcomeCategory,
  type OpportunityDataPoint,
  type VisibleTimeRange,
} from "./formatters";
import {
  calculateChartWidth,
  getVisibleTimeRange,
  getZeroValuePeriods,
} from "./utils";

/**
 * Custom hook to manage chart data and configuration
 * This demonstrates an alternative approach to organizing the chart calculations
 */
export function useChartData(
  timelineData: ActivityTimelineData | undefined,
  opportunities: Opportunity[] | undefined,
  firstTouchpointData: FirstTouchpointResponse | undefined,
  customTimeRange: { start: Date; end: Date } | null,
  getPersonById?: (id: string) => Person | undefined,
) {
  // Calculate the visible time range based on selection
  const visibleTimeRange = useMemo(
    () =>
      getVisibleTimeRange(
        timelineData,
        opportunities,
        firstTouchpointData,
        customTimeRange,
      ),
    [timelineData, opportunities, firstTouchpointData, customTimeRange],
  );

  // Calculate chart width based on data range and window size
  const chartWidth = useMemo(
    () => calculateChartWidth(visibleTimeRange, customTimeRange),
    [visibleTimeRange, customTimeRange],
  );

  // Format activity chart data
  const activityChartData = useMemo(
    () => formatActivityChartData(timelineData, visibleTimeRange),
    [timelineData, visibleTimeRange],
  );

  // Format opportunity data
  const opportunityData = useMemo(
    () => formatOpportunityData(opportunities, visibleTimeRange),
    [opportunities, visibleTimeRange],
  );

  // Calculate zero-value periods for overlays
  const zeroValuePeriods = useMemo(
    () => getZeroValuePeriods(activityChartData),
    [activityChartData],
  );

  const windowWidth = useWindowWidth();

  // Calculate shared domain and ticks for all charts
  const { domain, ticks, tickFormatter } = useMemo(
    () => formatChartDomainAndTicks(visibleTimeRange, windowWidth),
    [visibleTimeRange, windowWidth],
  );

  // Format first touchpoints data
  const formattedFirstTouchpoints = useMemo(
    () =>
      formatFirstTouchpoints(
        firstTouchpointData,
        visibleTimeRange,
        getPersonById || (() => undefined),
        customTimeRange,
      ),
    [firstTouchpointData, visibleTimeRange, getPersonById, customTimeRange],
  );

  return {
    visibleTimeRange,
    chartWidth,
    activityChartData,
    opportunityData,
    zeroValuePeriods,
    domain,
    ticks,
    tickFormatter,
    formattedFirstTouchpoints,
  };
}

// Enhanced data point interface to include outcome information
interface EnhancedOpportunityDataPoint extends OpportunityDataPoint {
  outcomeOpportunities?: Array<{
    id: string;
    name: string;
    stage: string;
    outcome: "won" | "lost" | "active";
    timestamp: number;
    y: number;
    stageTransitions?: Array<{
      stage: string;
      timestamp: number;
    }>;
  }>;
}

export function useOpportunityChartData(
  visibleTimeRange: VisibleTimeRange | null,
  accountId?: string,
  opportunitiesOverride?: Opportunity[],
) {
  const {
    selectedOpportunityIds,
    selectedPersonIds,
    buyingGroupMode,
    activeCustomGroupOpportunityIds, // Get the custom group opportunity IDs
    activeCustomGroupPersonIds, // Also get the custom group person IDs
  } = useBuyingGroupSelection();

  // Determine which person IDs to use based on mode
  const effectivePersonIds =
    buyingGroupMode === "custom"
      ? activeCustomGroupPersonIds
      : selectedPersonIds.length > 0
        ? selectedPersonIds
        : undefined;

  // Use the API hook with the correct IDs based on mode (skip if override provided)
  const { data: opportunitiesFromApi } = useAccountOpportunities(
    opportunitiesOverride ? undefined : accountId, // Skip fetch if using override
    // In custom mode, don't pass selectedOpportunityIds to avoid duplication
    buyingGroupMode === "custom"
      ? undefined
      : selectedOpportunityIds.length > 0
        ? selectedOpportunityIds
        : undefined,
    effectivePersonIds,
    buyingGroupMode, // Pass the buying group mode
    // Use explicit opportunity IDs for custom mode
    buyingGroupMode === "custom" ? activeCustomGroupOpportunityIds : undefined,
  );

  // Use override if provided, otherwise use API data
  const opportunities = opportunitiesOverride ?? opportunitiesFromApi;

  // Format opportunity data
  const opportunityData = useMemo(
    () => formatOpportunityData(opportunities, visibleTimeRange),
    [opportunities, visibleTimeRange],
  );

  // Process stage transitions for an opportunity
  const processStageTransitions = useCallback(
    (opp: Opportunity): Array<{ stage: string; timestamp: number }> => {
      // Get all stage transitions including the initial stage
      const stageTransitions: Array<{ stage: string; timestamp: number }> = [];

      // Add initial stage (creation)
      stageTransitions.push({
        stage: opp.stage_name,
        timestamp: getTimestamp(opp.created_date),
      });

      // Add all transitions
      if (opp.stage_transitions?.length) {
        opp.stage_transitions.forEach((transition) => {
          stageTransitions.push({
            stage: transition.event_name.replace("Changed stage to ", ""),
            timestamp: getTimestamp(transition.timestamp),
          });
        });
      }

      // Sort transitions by timestamp to ensure correct order
      stageTransitions.sort((a, b) => a.timestamp - b.timestamp);

      // Extract unique stages in order (keep the last occurrence of each stage)
      const uniqueStages: Array<{ stage: string; timestamp: number }> = [];
      const seenStages = new Set<string>();

      // Process in reverse to keep the last occurrence of each stage
      [...stageTransitions].reverse().forEach((transition) => {
        if (!seenStages.has(transition.stage)) {
          seenStages.add(transition.stage);
          uniqueStages.unshift(transition); // Add to front since we're processing in reverse
        }
      });

      return uniqueStages;
    },
    [],
  );

  // Enhance opportunity data with outcome information
  const enhancedOpportunityData = useMemo(() => {
    if (!opportunities?.length || !opportunityData.length) {
      return opportunityData;
    }

    // Create a deep copy of the opportunity data to avoid mutating the original
    const enhancedData = JSON.parse(
      JSON.stringify(opportunityData),
    ) as EnhancedOpportunityDataPoint[];

    // Get all unique opportunities with closed outcomes
    const closedOpps = opportunities
      .filter(
        (opp) =>
          opp.close_date &&
          getOutcomeCategory(opp.is_closed, opp.is_won) !== "active",
      )
      .map((opp) => {
        // Process stage transitions
        const stageTransitions = processStageTransitions(opp);

        return {
          id: opp.id,
          name: opp.name,
          stage: opp.stage_name,
          outcome: getOutcomeCategory(opp.is_closed, opp.is_won),
          timestamp: getTimestamp(opp.close_date!),
          stageTransitions,
        };
      });

    // For each closed opportunity, find or create a data point in our chart data
    closedOpps.forEach((closedOpp) => {
      // Find the data point with the closest timestamp
      const closestIndex = enhancedData.findIndex(
        (d) => Math.abs(d.timestamp - closedOpp.timestamp) < 86400000, // Within 1 day
      );

      if (closestIndex >= 0) {
        // Add the outcome information to the existing data point
        if (!enhancedData[closestIndex].outcomeOpportunities) {
          enhancedData[closestIndex].outcomeOpportunities = [];
        }

        // Find the opportunity's y position from the chart data
        const oppInChart = opportunityData
          .flatMap((d) => d.opportunities || [])
          .find((o) => o.id === closedOpp.id);

        if (oppInChart) {
          enhancedData[closestIndex].outcomeOpportunities!.push({
            ...closedOpp,
            y: oppInChart.y,
          });
        }
      }
    });

    return enhancedData;
  }, [opportunities, opportunityData, processStageTransitions]);

  return {
    visibleTimeRange,
    opportunityData: enhancedOpportunityData,
    processStageTransitions,
    opportunities,
  };
}

interface FirstTouchpointsChartDataProps {
  visibleTimeRange: VisibleTimeRange | null;
  customTimeRange: { start: Date; end: Date } | null;
  accountId?: string;
  getPersonById?: (id: string) => Person | undefined;
}
export function useFirstTouchpointsChartData({
  visibleTimeRange,
  customTimeRange,
  accountId,
  getPersonById,
}: FirstTouchpointsChartDataProps) {
  const {
    selectedOpportunityIds,
    selectedPersonIds,
    buyingGroupMode,
    activeCustomGroupOpportunityIds, // Get the custom group opportunity IDs
    activeCustomGroupPersonIds, // Also get the custom group person IDs
  } = useBuyingGroupSelection();

  // Determine which person IDs to use based on mode
  const effectivePersonIds =
    buyingGroupMode === "custom"
      ? activeCustomGroupPersonIds
      : selectedPersonIds.length > 0
        ? selectedPersonIds
        : undefined;

  const { data: firstTouchpointData } = useAccountFirstTouchpointTimeline(
    accountId,
    // In custom mode, don't pass selectedOpportunityIds to avoid duplication
    buyingGroupMode === "custom"
      ? undefined
      : selectedOpportunityIds.length > 0
        ? selectedOpportunityIds
        : undefined,
    effectivePersonIds,
    buyingGroupMode, // Pass the buying group mode
    // Use explicit opportunity IDs for custom mode
    buyingGroupMode === "custom" ? activeCustomGroupOpportunityIds : undefined,
  );

  // Format first touchpoints data
  const formattedFirstTouchpoints = useMemo(
    () =>
      formatFirstTouchpoints(
        firstTouchpointData,
        visibleTimeRange,
        getPersonById || (() => undefined),
        customTimeRange,
      ),
    [firstTouchpointData, visibleTimeRange, getPersonById, customTimeRange],
  );

  return {
    visibleTimeRange,
    formattedFirstTouchpoints,
  };
}
